import { useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, type TooltipProps } from 'recharts';
import { ChartContainer, type ChartConfig } from '../../../../components/ui/Chart';
import { fCurrency } from '../../../../utils/formatNumber';

// ----------------------------------------------------------------------

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2, 173 58% 39%))',
  'hsl(var(--chart-3, 197 37% 24%))',
  'hsl(var(--chart-4, 43 74% 66%))',
  'hsl(var(--chart-5, 27 87% 67%))'
];

interface ChartDonutSubMonthCurrencyOrValueProps {
  labelStrings: string[];
  series: number[];
  totalSeriesValue: number;
  currencyMode?: boolean;
  rawTotalValue?: number | null;
}

export default function ChartDonutSubMonthCurrencyOrValue({
  labelStrings,
  series,
  totalSeriesValue,
  currencyMode = false,
  rawTotalValue
}: ChartDonutSubMonthCurrencyOrValueProps) {
  const chartConfig: ChartConfig = {};
  labelStrings.forEach((name, i) => {
    chartConfig[name] = { label: name, color: CHART_COLORS[i % CHART_COLORS.length] };
  });

  const data = useMemo(
    () =>
      labelStrings.map((name, i) => ({
        name,
        value: Math.round(series[i] ?? 0)
      })),
    [labelStrings, series]
  );

  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);

  const displayTotal = selectedSegment
    ? data.find((d) => d.name === selectedSegment)?.value ?? 0
    : rawTotalValue ?? totalSeriesValue;
  const centerLabel = currencyMode ? fCurrency(displayTotal) : displayTotal.toFixed(0);

  const handleSegmentSelect = (name: string) => {
    setSelectedSegment((prev) => (prev === name ? null : name));
  };

  const handlePieClick = (entry: { name?: string }) => {
    const name = entry?.name;
    if (name != null) handleSegmentSelect(name);
  };

  const renderLegend = () => (
    <div className="flex flex-wrap justify-center gap-4">
      {labelStrings.map((name, i) => {
        const isSelected = selectedSegment === null || selectedSegment === name;
        const color = CHART_COLORS[i % CHART_COLORS.length];
        return (
          <button
            key={name}
            type="button"
            className="flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-50"
            style={{ opacity: isSelected ? 1 : 0.5 }}
            onClick={() => handleSegmentSelect(name)}
            onKeyDown={(e) => e.key === 'Enter' && handleSegmentSelect(name)}
          >
            <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            <span>{name}</span>
          </button>
        );
      })}
    </div>
  );

  const renderTooltip: TooltipProps<number, string>['content'] = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const value = item.value ?? 0;
    const percent = totalSeriesValue > 0 ? (value / totalSeriesValue) * 100 : 0;
    const percentSubtext = `${percent.toFixed(0)}% of total`;
    return (
      <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-md">
        <div className="font-medium">{item.name ?? ''}</div>
        {currencyMode ? (
          <>
            <div>{fCurrency(value)}</div>
            <div className="text-muted-foreground">{percentSubtext}</div>
          </>
        ) : (
          <>
            <div>{value.toLocaleString()}</div>
            <div className="text-muted-foreground">{percentSubtext}</div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-[380px] w-full min-h-[380px] flex-col overflow-visible">
      <ChartContainer
        config={chartConfig}
        className="min-h-0 flex-1 [&_.recharts-responsive-container]:outline-none [&_.recharts-responsive-container]:focus:outline-none [&_.recharts-responsive-container]:ring-0 [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:focus:outline-none [&_.recharts-wrapper]:ring-0 [&_.recharts-pie-sector]:outline-none [&_.recharts-pie-sector]:focus:outline-none [&_.recharts-pie-sector]:ring-0 [&_.recharts-pie]:outline-none [&_.recharts-pie]:focus:outline-none [&_.recharts-pie]:ring-0"
      >
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="65%"
            outerRadius="90%"
            paddingAngle={2}
            label={false}
            onClick={handlePieClick}
          >
            {data.map((d) => {
              const isSelected = selectedSegment === null || selectedSegment === d.name;
              const fill = CHART_COLORS[labelStrings.indexOf(d.name) % CHART_COLORS.length];
              return (
                <Cell
                  key={d.name}
                  fill={fill}
                  opacity={isSelected ? 1 : 0.35}
                  strokeOpacity={isSelected ? 1 : 0.35}
                />
              );
            })}
          </Pie>
          <Tooltip content={renderTooltip} />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-foreground text-sm font-medium"
          >
            {centerLabel}
          </text>
        </PieChart>
      </ChartContainer>
      <div className="shrink-0 pt-2">{renderLegend()}</div>
    </div>
  );
}
